import React, { useMemo } from 'react';
import { Alert, AlertIcon, Skeleton } from '@chakra-ui/react';
import { orderBy, sumBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getIconForDevice } from '../../../devices/devices-helper';
import { SenseAccordionItem } from '../common/SenseAccordionItem';
import { SensePowerCard } from '../common/SensePowerCard';
import { LiveDeviceWithCost, useLiveDevicesWithCost } from '../common/use-live-devices-with-cost';
import { MAX_NUMBER_OF_TOP_CONSUMER_DEVICES, OTHER_DEVICES_COLOR } from '../constants';

interface PowerConsumersProps {
  readonly isDialChartSelected: boolean;
}

export function PowerConsumers({ isDialChartSelected }: PowerConsumersProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { liveData, costsData, devicesWithCost, isLoaded, isError } = useLiveDevicesWithCost();

  const devices = useMemo(() => {
    const { consumption } = liveData;
    const cost = costsData.costData[0]?.total_cost || 0;

    const notTopDevices: LiveDeviceWithCost[] = orderBy(devicesWithCost, (device) => device.power, 'desc')
      .slice(MAX_NUMBER_OF_TOP_CONSUMER_DEVICES)
      .filter((device) => device.power > 0);

    const otherLoads: LiveDeviceWithCost = {
      switchId: null,
      applianceId: null,
      assignment: 'load_other',
      name: t('Home Screen.other devices'),
      power: consumption - (sumBy(devicesWithCost, (device) => device.power) ?? 0),
      cost: cost - (sumBy(devicesWithCost, (device) => device.cost) ?? 0),
    };

    return [...notTopDevices, otherLoads];
  }, [liveData, costsData.costData, devicesWithCost, t]);

  const handleCardClick = (device: LiveDeviceWithCost) => {
    if (!device?.switchId && !device?.applianceId) {
      navigate('../devices/list');
    } else {
      navigate(
        device.switchId
          ? `../switches/${device.switchId}/view?backURL=../home`
          : `../devices/${device.applianceId}/view?backURL=../home`
      );
    }
  };

  const otherPowerConsumersTitle = isDialChartSelected
    ? t('Home Sense.other power consumers dial chart')
    : t('Home Sense.other power consumers line chart');

  return (
    <SenseAccordionItem title={otherPowerConsumersTitle} dataTestId="rest-of-home-list">
      <Skeleton
        isLoaded={isLoaded}
        borderRadius={5}
        borderBottomRightRadius="25px"
        height={isLoaded ? 'unset' : 75}
        my={4}
      >
        {isError ? (
          <Alert status="error" variant="left-accent" data-testid="error-alert">
            <AlertIcon />
            {t('Common.error fetching data')}
          </Alert>
        ) : (
          devices.map((device, i) => (
            <SensePowerCard
              onClick={() => handleCardClick(device)}
              borderColor={OTHER_DEVICES_COLOR}
              name={device.name}
              power={device.power}
              cost={device.cost}
              Icon={getIconForDevice(device.assignment)}
              key={i}
              dataTestId={`rest-of-home-list-item-${device.name.toLowerCase().replace(' ', '-')}-${i}`}
            />
          ))
        )}
      </Skeleton>
    </SenseAccordionItem>
  );
}
